import { ref, computed, type Ref, type ComputedRef } from 'vue';
import { useDebounceFn, useResizeObserver } from '@vueuse/core';
import type { Participant } from '@/types/participant.js';
import type { CSSProperties } from 'vue';

export interface BoardDimensions {
    width: number;
    height: number;
    radius: number;
    x: number;
    y: number;
    centerX: number;
    centerY: number;
}

export type PositionStyles = CSSProperties;

export type CardPositionStyles = CSSProperties;

export interface UseParticipantLayoutOptions {
    /** Reference to the board element */
    boardRef: Ref<HTMLElement | null | undefined>;
    /** List of participants to position */
    participants: Ref<Participant[]> | ComputedRef<Participant[]>;
    /** Margin from center for initial position (avatar) */
    initialMargin?: number;
    /** Margin from center for final position (card when voted) */
    finalMargin?: number;
}

/**
 * Composable for calculating participant positions on the circular session board.
 *
 * Positions participants in a circle around the board center, with configurable
 * margins for avatar and card positions.
 *
 * @example
 * ```ts
 * const boardRef = useTemplateRef('board');
 * const {
 *   getParticipantPositionStyles,
 *   getCardFinalPositionStyles,
 *   readyToRenderCards
 * } = useParticipantLayout({
 *   boardRef,
 *   participants: computed(() => props.participants),
 * });
 * ```
 */
export function useParticipantLayout(options: UseParticipantLayoutOptions) {
    const { boardRef, participants, initialMargin = 60, finalMargin = -40 } = options;

    const boardCenterX = ref(0);
    const boardCenterY = ref(0);
    const boardRadius = ref(0);
    const readyToRenderCards = ref(false);

    /**
     * Get the dimensions of the board element.
     */
    function getBoardDimensions(): BoardDimensions {
        const dim = boardRef.value?.getBoundingClientRect();
        return {
            width: dim?.width ?? 0,
            height: dim?.height ?? 0,
            radius: Math.min(dim?.width ?? 0, dim?.height ?? 0) / 2,
            x: dim?.x ?? 0,
            y: dim?.y ?? 0,
            centerX: ((dim?.x ?? 0) + (dim?.width ?? 0)) / 2,
            centerY: ((dim?.y ?? 0) + (dim?.height ?? 0)) / 2,
        };
    }

    /**
     * Update the cached board center and radius. Call this on mount and resize.
     */
    function updateBoardCenter(): void {
        const dim = getBoardDimensions();
        // When the board has non-zero dimensions, we can assume it has been rendered
        if (dim.width !== 0 && dim.height !== 0) {
            readyToRenderCards.value = true;
        }
        boardCenterX.value = dim.width / 2;
        boardCenterY.value = dim.height / 2;
        boardRadius.value = dim.radius;
    }

    /**
     * Get the index of a participant in the participants list.
     */
    function getParticipantIndex(participant: Participant): number {
        return participants.value.findIndex((p) => p.id === participant.id);
    }

    /**
     * Calculate position styles for an element relative to a center point.
     * Positions elements in a circle around the center.
     * Uses the cached board radius (updated by updateBoardCenter / resize observer)
     * to avoid repeated getBoundingClientRect() calls.
     */
    function calculateCircularPosition(
        participantIndex: number,
        totalParticipants: number,
        centerX: number,
        centerY: number,
        margin: number,
    ): PositionStyles {
        if (participantIndex === -1 || totalParticipants === 0) return {};

        const radius = boardRadius.value + margin;
        const angle = ((2 * Math.PI) / totalParticipants) * participantIndex + Math.PI / 2;
        const x = centerX + radius * Math.cos(angle);
        const y = centerY + radius * Math.sin(angle);

        return {
            left: `${x}px`,
            top: `${y}px`,
        };
    }

    /**
     * Get position styles for a participant's avatar.
     */
    function getParticipantPositionStyles(participant: Participant): PositionStyles {
        if (!boardRef.value) return {};

        const idx = getParticipantIndex(participant);
        return calculateCircularPosition(
            idx,
            participants.value.length,
            boardCenterX.value,
            boardCenterY.value,
            initialMargin,
        );
    }

    /**
     * Get position styles for a participant's card, including CSS variables
     * for initial and final positions (used for vote animation).
     */
    function getCardFinalPositionStyles(participant: Participant): CardPositionStyles {
        const idx = getParticipantIndex(participant);
        const total = participants.value.length;

        const initialPosition = calculateCircularPosition(
            idx,
            total,
            boardCenterX.value,
            boardCenterY.value,
            initialMargin,
        );

        const finalPosition = calculateCircularPosition(
            idx,
            total,
            boardCenterX.value,
            boardCenterY.value,
            finalMargin,
        );

        return {
            display: readyToRenderCards.value ? '' : 'none',
            '--initial-position-x': `calc(${initialPosition.left} - 50%)`,
            '--initial-position-y': `calc(${initialPosition.top} - 50%)`,
            '--final-position-x': `calc(${finalPosition.left} - 50%)`,
            '--final-position-y': `calc(${finalPosition.top} - 50%)`,
        };
    }

    // Set up resize observer with debounce
    useResizeObserver(
        boardRef,
        useDebounceFn(() => {
            updateBoardCenter();
        }, 50),
    );

    return {
        // State
        boardCenterX: computed(() => boardCenterX.value),
        boardCenterY: computed(() => boardCenterY.value),
        readyToRenderCards: computed(() => readyToRenderCards.value),

        // Methods
        updateBoardCenter,
        getBoardDimensions,
        getParticipantIndex,
        getParticipantPositionStyles,
        getCardFinalPositionStyles,
        calculateCircularPosition,
    };
}
